import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, isElevated } from "@/contexts/AuthContext";
import { FLAG_PASSAGGIO, haFlag } from "@/lib/ambito";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import CurrencyInput from "@/components/CurrencyInput";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import { FileSignature, Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const CATEGORIE = [
  { value: "autoveicoli", label: "Autoveicoli" },
  { value: "motoveicoli", label: "Motoveicoli" },
  { value: "veicoli_commerciali", label: "Veicoli commerciali" },
  { value: "auto_storica", label: "Auto storica" },
  { value: "roulotte_camper", label: "Roulotte / Camper" },
  { value: "altro", label: "Altro" },
];

const TIPI_ORIGINE = [
  { value: "nazionale", label: "Nazionale" },
  { value: "importazione", label: "Importazione" },
];

const COSTI_FISSI_FIELDS = [
  { key: "emolumenti_aci", label: "Emolumenti ACI" },
  { key: "imposta_bollo_pra", label: "Imposta bollo PRA" },
  { key: "diritti_dt", label: "Diritti DT" },
  { key: "imposta_bollo_carta_circolazione", label: "Imposta bollo carta circolazione" },
  { key: "recupero_commissioni", label: "Recupero commissioni" },
  { key: "costo_marca_bollo", label: "Costo marca bollo" },
  { key: "costo_incasso_pra", label: "Costo incasso PRA" },
];

function emptyConfig(categoria, tipo) {
  return {
    categoria_veicolo: categoria,
    tipo_origine: tipo,
    ipt_modalita: "automatico",
    ipt_tabella: [],
    ipt_calcolo_base: 0, ipt_limite_kw: 0,
    ipt_importo_oltre_kw: 0, ipt_maggiorazione_provinciale: 0,
    // Sprint 4F2.3 C2/C3 — Tabellato refactored
    ipt_maggiorazione_default: 30,
    ipt_maggiorazione_tabella: [],
    costi_fissi_modalita: "dettaglio",
    costi_fissi_dettaglio: Object.fromEntries(COSTI_FISSI_FIELDS.map((f) => [f.key, 0])),
    costi_fissi_totale: null,
    competenze_agenzia_imponibile: 0,
    ricarico_imponibile: 0,
    targhe: 0, spese_spedizione: 0,
  };
}


export default function PassaggioProprietaSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const elevated = isElevated(user);
  // Sprint CRM-5 — anche chi ha la casella «gestione_config_passaggio», con le sole aziende in cui ce l'ha
  const consentito = elevated || haFlag(user, FLAG_PASSAGGIO);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [categoria, setCategoria] = useState("autoveicoli");
  const [tipo, setTipo] = useState("nazionale");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existing, setExisting] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Sprint 4F2.3 C4 — Import-from modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importSource, setImportSource] = useState({
    source_company_id: null,
    source_categoria_veicolo: "autoveicoli",
    source_tipo_origine: "nazionale",
  });

  useEffect(() => {
    if (!consentito) return;
    api.get("/companies").then((r) => {
      const items = (r.data.items || r.data || []).filter((c) => elevated || haFlag(user, FLAG_PASSAGGIO, c.id));
      setCompanies(items);
      if (!companyId && items.length > 0) setCompanyId(items[0].id);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentito]);

  // Fetch config when key changes
  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api.get(`/companies/${companyId}/passaggio-config/${categoria}/${tipo}`)
      .then((r) => {
        setConfig(r.data);
        setExisting(true);
        setDirty(false);
      })
      .catch(() => {
        setConfig(emptyConfig(categoria, tipo));
        setExisting(false);
        setDirty(false);
      })
      .finally(() => setLoading(false));
  }, [companyId, categoria, tipo]);

  const setField = (patch) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
  };

  const setCostoField = (key, value) => {
    setConfig((c) => ({
      ...c,
      costi_fissi_dettaglio: { ...(c.costi_fissi_dettaglio || {}), [key]: value },
    }));
    setDirty(true);
  };

  const handleChangeCategoria = (newCat) => {
    if (dirty && !window.confirm("Modifiche non salvate. Vuoi uscire?")) return;
    setCategoria(newCat);
  };
  const handleChangeTipo = (newT) => {
    if (dirty && !window.confirm("Modifiche non salvate. Vuoi uscire?")) return;
    setTipo(newT);
  };

  const save = async () => {
    if (!companyId || !config) return;
    try {
      const payload = sanitize(config);
      if (existing) {
        await api.patch(`/companies/${companyId}/passaggio-config/${config.id}`, payload);
        toast.success("Configurazione aggiornata");
      } else {
        const r = await api.post(`/companies/${companyId}/passaggio-config`, payload);
        setConfig(r.data);
        setExisting(true);
        toast.success("Configurazione creata");
      }
      setDirty(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore salvataggio");
    }
  };

  const remove = async () => {
    if (!existing || !config?.id) return;
    if (!window.confirm("Eliminare questa configurazione?")) return;
    try {
      await api.delete(`/companies/${companyId}/passaggio-config/${config.id}`);
      toast.success("Configurazione eliminata");
      setConfig(emptyConfig(categoria, tipo));
      setExisting(false);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  const doImport = async () => {
    if (!existing || !config?.id) {
      toast.error("Salva prima la configurazione corrente, poi importa.");
      return;
    }
    if (!importSource.source_company_id) {
      toast.error("Seleziona azienda sorgente"); return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm("Importazione sovrascriverà tutti i valori correnti. Confermi?")) return;
    try {
      const r = await api.post(
        `/companies/${companyId}/passaggio-config/${config.id}/import-from`,
        importSource,
      );
      setConfig(r.data);
      setDirty(false);
      setImportOpen(false);
      toast.success("Valori importati dalla configurazione sorgente");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore importazione");
    }
  };

  if (!consentito) {
    return (
      <Card className="p-6 text-sm text-slate-500 text-center" data-testid="passaggio-settings-no-perm">
        Permesso "Gestione configurazioni" richiesto per accedere a questa pagina.
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="passaggio-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/settings")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Settings
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <FileSignature className="h-6 w-6 text-blue-600" /> Passaggio di proprietà
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Imposta i valori di default per ogni categoria veicolo e tipo origine.
              I valori saranno proposti automaticamente nelle pratiche di passaggio.
            </p>
          </div>
        </div>
        <div className="min-w-[260px]">
          <Label className="text-xs">Azienda</Label>
          <Select value={companyId || ""} onValueChange={setCompanyId}>
            <SelectTrigger data-testid="psg-company-select">
              <SelectValue placeholder="Seleziona azienda" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs Categoria */}
      <Card className="p-3">
        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Categoria veicolo</div>
        <div className="flex flex-wrap gap-2" data-testid="psg-tabs-categoria">
          {CATEGORIE.map((c) => (
            <button
              key={c.value}
              onClick={() => handleChangeCategoria(c.value)}
              data-testid={`psg-tab-${c.value}`}
              data-active={categoria === c.value}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${categoria === c.value
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-3 text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Tipo origine</div>
        <div className="flex gap-2" data-testid="psg-tabs-tipo">
          {TIPI_ORIGINE.map((t) => (
            <button
              key={t.value}
              onClick={() => handleChangeTipo(t.value)}
              data-testid={`psg-tab-tipo-${t.value}`}
              data-active={tipo === t.value}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${tipo === t.value
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      {loading || !config ? (
        <Card className="p-6 text-center text-slate-500">Caricamento…</Card>
      ) : (
        <>
          <Card className="p-4 flex items-center justify-between border-l-4 border-blue-400">
            <div>
              <div className="text-base font-semibold">
                {CATEGORIE.find((c) => c.value === categoria)?.label}
                {" · "}{TIPI_ORIGINE.find((t) => t.value === tipo)?.label}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {existing ? "Configurazione esistente — modificabile" : "Nuova configurazione (non ancora salvata)"}
                {dirty && <span className="text-amber-700 ml-2">· Modifiche non salvate</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {existing && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                          data-testid="psg-btn-import">
                    <FileSignature className="h-4 w-4 mr-1" /> Importa valori da…
                  </Button>
                  <Button variant="outline" size="sm" onClick={remove} className="text-rose-600">
                    <Trash2 className="h-4 w-4 mr-1" /> Elimina
                  </Button>
                </>
              )}
              <Button size="sm" onClick={save} disabled={!dirty && existing} data-testid="psg-save">
                <Save className="h-4 w-4 mr-1" /> {existing ? "Salva modifiche" : "Crea configurazione"}
              </Button>
            </div>
          </Card>

          <SezioneIPT config={config} setField={setField} />
          <SezioneCostiFissi config={config} setField={setField} setCostoField={setCostoField} />
          <SezioneCompetenzeRicarico config={config} setField={setField} />
          <SezioneAltri config={config} setField={setField} />
          <SezioneCodiciIva config={config} setField={setField} />
        </>
      )}

      {/* Sprint 4F2.3 C4 — Modal "Importa valori da..." */}
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        companies={companies}
        currentCompanyId={companyId}
        source={importSource}
        setSource={setImportSource}
        onSubmit={doImport}
      />
    </div>
  );
}

function ImportModal({ open, onClose, companies, currentCompanyId, source, setSource, onSubmit }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
         data-testid="psg-import-modal">
      <Card className="w-[480px] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold border-b pb-2">Importa configurazione da…</h3>
        <p className="text-xs text-slate-500">
          Copierà <strong>tutti i valori</strong> della configurazione sorgente (IPT, costi fissi, competenze, ricarico, targhe, spedizione) sulla configurazione corrente. Sovrascrive i valori esistenti.
        </p>

        <div>
          <Label className="text-xs">Azienda sorgente</Label>
          <Select value={source.source_company_id || ""}
                  onValueChange={(v) => setSource({ ...source, source_company_id: v })}>
            <SelectTrigger data-testid="psg-import-azienda">
              <SelectValue placeholder="Seleziona azienda…" />
            </SelectTrigger>
            <SelectContent>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.id === currentCompanyId ? " (corrente)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Categoria veicolo</Label>
            <Select value={source.source_categoria_veicolo}
                    onValueChange={(v) => setSource({ ...source, source_categoria_veicolo: v })}>
              <SelectTrigger data-testid="psg-import-categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIE.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Tipo origine</Label>
            <Select value={source.source_tipo_origine}
                    onValueChange={(v) => setSource({ ...source, source_tipo_origine: v })}>
              <SelectTrigger data-testid="psg-import-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPI_ORIGINE.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button size="sm" onClick={onSubmit} className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="psg-import-submit">
            Importa
          </Button>
        </div>
      </Card>
    </div>
  );
}


function sanitize(c) {
  const out = { ...c };
  // Remove fields server-side managed
  for (const k of ["id", "company_id", "created_at", "created_by",
                    "updated_at", "updated_by", "deleted_at"]) {
    delete out[k];
  }
  // Sprint 4F2.3 C2: in `tabellato` ora servono gli stessi campi di `automatico`
  // + ipt_maggiorazione_default + ipt_maggiorazione_tabella.
  if (out.ipt_modalita === "automatico") {
    out.ipt_tabella = null;
    out.ipt_maggiorazione_default = null;
    out.ipt_maggiorazione_tabella = null;
  }
  if (out.ipt_modalita === "manuale") {
    out.ipt_tabella = null;
    out.ipt_calcolo_base = null;
    out.ipt_limite_kw = null;
    out.ipt_importo_oltre_kw = null;
    out.ipt_maggiorazione_provinciale = null;
    out.ipt_maggiorazione_default = null;
    out.ipt_maggiorazione_tabella = null;
  }
  if (out.ipt_modalita === "tabellato") {
    out.ipt_tabella = null;  // legacy field — non più usato
    out.ipt_maggiorazione_provinciale = null;  // sostituito da default + tabella
  }
  if (out.costi_fissi_modalita === "dettaglio") out.costi_fissi_totale = null;
  if (out.costi_fissi_modalita === "totale_unico") out.costi_fissi_dettaglio = null;
  return out;
}


function SezioneIPT({ config, setField }) {
  const m = config.ipt_modalita;
  return (
    <Card className="p-5" data-testid="psg-sezione-ipt">
      <h3 className="text-lg font-bold mb-3 pb-1 border-b">IPT (Imposta provinciale di trascrizione)</h3>

      <div className="flex flex-wrap gap-5 mb-4">
        {[
          { v: "tabellato", label: "Tabellato (per provincia)" },
          { v: "automatico", label: "Automatico (formula)" },
          { v: "manuale", label: "Manuale (per pratica)" },
        ].map((o) => (
          <label key={o.v} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" checked={m === o.v}
              onChange={() => setField({ ipt_modalita: o.v })}
              data-testid={`psg-ipt-mod-${o.v}`} />
            <span className="text-sm font-medium">{o.label}</span>
          </label>
        ))}
      </div>

      {m === "automatico" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="psg-ipt-auto-block">
          <NumberField label="IPT Base (€)" testId="psg-ipt-base"
            value={config.ipt_calcolo_base}
            onChange={(v) => setField({ ipt_calcolo_base: v })} currency />
          <NumberField label="Limite kW (≤ paga base)" testId="psg-ipt-limite"
            value={config.ipt_limite_kw}
            onChange={(v) => setField({ ipt_limite_kw: v })} int />
          <NumberField label="€/kW oltre limite" testId="psg-ipt-oltre"
            value={config.ipt_importo_oltre_kw}
            onChange={(v) => setField({ ipt_importo_oltre_kw: v })} currency step="0.0001" decimals={4} />
          <NumberField label="Maggiorazione %" testId="psg-ipt-magg"
            value={config.ipt_maggiorazione_provinciale}
            onChange={(v) => setField({ ipt_maggiorazione_provinciale: v })} suffix="%" />
          <div className="col-span-2 md:col-span-4 text-[11px] text-slate-500 italic">
            Formula: <code>se kW ≤ limite → IPT = Base × (1 + magg/100); se kW &gt; limite → IPT = kW × €/kW × (1 + magg/100)</code>
          </div>
        </div>
      )}

      {m === "tabellato" && (
        <SezioneIPTTabella config={config} setField={setField} />
      )}

      {m === "manuale" && (
        <div className="text-sm text-slate-500 italic" data-testid="psg-ipt-manuale-info">
          Nessun parametro da configurare. L'IPT verrà inserito manualmente in ogni pratica di passaggio.
        </div>
      )}
    </Card>
  );
}


function SezioneIPTTabella({ config, setField }) {
  // Sprint 4F2.3 C2/C3 — Tabellato refactored: stessi 3 campi di automatico
  // + maggiorazione_default + tabella province × maggiorazione%
  const tabella = config.ipt_maggiorazione_tabella || [];
  const updateRow = (idx, key, value) => {
    const nt = [...tabella];
    nt[idx] = { ...nt[idx], [key]: value };
    setField({ ipt_maggiorazione_tabella: nt });
  };
  const addRow = () => setField({
    ipt_maggiorazione_tabella: [...tabella, { provincia: "", maggiorazione: 0 }],
  });
  const removeRow = (idx) => setField({
    ipt_maggiorazione_tabella: tabella.filter((_, i) => i !== idx),
  });
  const resetDefault = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Sovrascriverai la tabella corrente con i 19 valori default. Continuare?")) return;
    try {
      const r = await api.get("/passaggio-config/default-maggiorazione-tabella");
      setField({
        ipt_maggiorazione_default: r.data.default_percent,
        ipt_maggiorazione_tabella: r.data.tabella,
      });
      toast.success("Tabella reimpostata ai default (19 province)");
    } catch (e) {
      toast.error("Errore reset tabella");
    }
  };
  return (
    <div data-testid="psg-ipt-tab-block" className="space-y-4">
      {/* Stessi 3 campi di automatico */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <NumberField label="IPT Base (€)" testId="psg-ipt-base-tab"
          value={config.ipt_calcolo_base}
          onChange={(v) => setField({ ipt_calcolo_base: v })} currency />
        <NumberField label="Limite kW (≤ paga base)" testId="psg-ipt-limite-tab"
          value={config.ipt_limite_kw}
          onChange={(v) => setField({ ipt_limite_kw: v })} int />
        <NumberField label="€/kW oltre limite" testId="psg-ipt-oltre-tab"
          value={config.ipt_importo_oltre_kw}
          onChange={(v) => setField({ ipt_importo_oltre_kw: v })} currency step="0.0001" decimals={4} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
        <NumberField label="Maggiorazione default %" testId="psg-ipt-magg-default"
          value={config.ipt_maggiorazione_default}
          onChange={(v) => setField({ ipt_maggiorazione_default: v })} suffix="%" />
        <div className="col-span-1 md:col-span-2 text-[11px] text-slate-500 italic">
          Applicata a tutte le province NON elencate nella tabella sotto.
        </div>
      </div>

      <div className="border-t pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wide font-medium text-slate-600">
            Maggiorazioni per provincia ({tabella.length})
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={resetDefault}
                    data-testid="psg-magg-reset-default">
              Reset a default (19 province)
            </Button>
            <Button size="sm" variant="outline" onClick={addRow}
                    data-testid="psg-magg-add">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi provincia
            </Button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase text-slate-500 border-b">
            <tr>
              <th className="text-left py-1 w-[140px]">Provincia (sigla)</th>
              <th className="text-left py-1">Maggiorazione %</th>
              <th className="w-[60px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tabella.map((r, i) => (
              <tr key={i}>
                <td className="py-1.5 pr-2">
                  <ProvinciaPicker
                    value={r.provincia}
                    onChange={(v) => updateRow(i, "provincia", (v || "").toUpperCase())}
                    testId={`psg-magg-prov-${i}`}
                    compact
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <Input type="number" step="0.01" min="0" max="200"
                    value={r.maggiorazione}
                    onChange={(e) => updateRow(i, "maggiorazione", parseFloat(e.target.value) || 0)}
                    data-testid={`psg-magg-val-${i}`} />
                </td>
                <td className="py-1.5 text-right">
                  <button onClick={() => removeRow(i)} className="text-slate-400 hover:text-rose-600 p-1"
                          data-testid={`psg-magg-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {tabella.length === 0 && (
              <tr><td colSpan={3} className="py-3 text-center text-slate-400 italic">
                Nessuna provincia esplicita: tutte usano la maggiorazione default ({config.ipt_maggiorazione_default ?? 30}%)
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-[11px] text-slate-500 italic">
        Formula: <code>se kW ≤ limite → IPT = Base × (1 + magg/100); se kW &gt; limite → IPT = kW × €/kW × (1 + magg/100)</code>. La % è quella della provincia in tabella; in mancanza si usa il default.
      </div>
    </div>
  );
}


function SezioneCostiFissi({ config, setField, setCostoField }) {
  const m = config.costi_fissi_modalita;
  const totDettaglio = useMemo(() => {
    const d = config.costi_fissi_dettaglio || {};
    return COSTI_FISSI_FIELDS.reduce((s, f) => s + (Number(d[f.key]) || 0), 0);
  }, [config.costi_fissi_dettaglio]);

  return (
    <Card className="p-5" data-testid="psg-sezione-costi">
      <h3 className="text-lg font-bold mb-3 pb-1 border-b">Costi fissi (esenti IVA)</h3>
      <div className="flex gap-5 mb-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={m === "dettaglio"}
            onChange={() => setField({ costi_fissi_modalita: "dettaglio" })}
            data-testid="psg-costi-mod-dettaglio" />
          <span className="text-sm font-medium">Dettaglio (7 voci)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" checked={m === "totale_unico"}
            onChange={() => setField({ costi_fissi_modalita: "totale_unico" })}
            data-testid="psg-costi-mod-totale" />
          <span className="text-sm font-medium">Totale unico (1 importo)</span>
        </label>
      </div>

      {m === "dettaglio" && (
        <div data-testid="psg-costi-dett-block">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {COSTI_FISSI_FIELDS.map((f) => (
              <NumberField key={f.key} label={f.label}
                testId={`psg-costo-${f.key}`}
                value={config.costi_fissi_dettaglio?.[f.key] || 0}
                onChange={(v) => setCostoField(f.key, v)} currency />
            ))}
          </div>
          <div className="mt-3 px-3 py-2 bg-slate-50 rounded-md flex justify-between text-sm">
            <span className="font-medium text-slate-600">Totale dettaglio:</span>
            <span className="font-mono font-bold text-slate-900">{totDettaglio.toFixed(2)} €</span>
          </div>
        </div>
      )}
      {m === "totale_unico" && (
        <div className="grid grid-cols-2 gap-3" data-testid="psg-costi-tot-block">
          <NumberField label="Totale unico costi fissi (€)"
            testId="psg-costi-totale-input"
            value={config.costi_fissi_totale || 0}
            onChange={(v) => setField({ costi_fissi_totale: v })} currency />
        </div>
      )}
    </Card>
  );
}


function SezioneCompetenzeRicarico({ config, setField }) {
  return (
    <Card className="p-5" data-testid="psg-sezione-competenze">
      <h3 className="text-lg font-bold mb-3 pb-1 border-b">Competenze agenzia e ricarico (imponibili)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <NumberField label="Competenze agenzia · imponibile"
          testId="psg-comp-imp"
          value={config.competenze_agenzia_imponibile}
          onChange={(v) => setField({ competenze_agenzia_imponibile: v })} currency />
        <NumberField label="Ricarico · imponibile"
          testId="psg-ric-imp"
          value={config.ricarico_imponibile}
          onChange={(v) => setField({ ricarico_imponibile: v })} currency />
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        Codici IVA visualizzati nella sezione "Codici IVA" in fondo alla pagina.
      </p>
    </Card>
  );
}


function SezioneAltri({ config, setField }) {
  return (
    <Card className="p-5" data-testid="psg-sezione-altri">
      <h3 className="text-lg font-bold mb-3 pb-1 border-b">Targhe e spedizione (esenti IVA)</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <NumberField label="Targhe (€)" testId="psg-targhe"
          value={config.targhe} onChange={(v) => setField({ targhe: v })} currency />
        <NumberField label="Spese spedizione (€)" testId="psg-spedizione"
          value={config.spese_spedizione} onChange={(v) => setField({ spese_spedizione: v })} currency />
      </div>
    </Card>
  );
}


function NumberField({ label, value, onChange, currency, int, suffix, step, decimals, testId }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {currency ? (
        <CurrencyInput value={value || 0} onChange={(v) => onChange(v ?? 0)}
          data-testid={testId} step={step} decimals={decimals} />
      ) : (
        <div className="relative">
          <Input type="number" step={step || (int ? "1" : "0.01")} min="0"
            value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0
              : (int ? parseInt(e.target.value, 10) : parseFloat(e.target.value)))}
            className={suffix ? "pr-7" : ""}
            data-testid={testId} />
          {suffix && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm pointer-events-none">{suffix}</span>
          )}
        </div>
      )}
    </div>
  );
}



// =============== Sprint 4I1.B3 — Sezione Codici IVA Settings ===============
function SezioneCodiciIva({ config, setField }) {
  const lockedRows = [
    { key: "ipt_iva_codice", label: "IPT", expected: "N1", desc: "Anticipazione art.15" },
    { key: "costi_fissi_iva_codice", label: "Costi fissi", expected: "N1", desc: "Anticipazione art.15" },
    { key: "targhe_iva_codice", label: "Targhe", expected: "N1", desc: "Anticipazione art.15" },
    { key: "ricarico_iva_codice", label: "Ricarico", expected: "I22", desc: "Default azienda" },
  ];
  return (
    <Card className="p-5" data-testid="psg-sezione-iva">
      <h3 className="text-lg font-bold mb-3 pb-1 border-b">Codici IVA</h3>
      <div className="text-sm space-y-3">
        <p className="text-xs text-slate-600">
          I codici IVA sono determinati dalla normativa fiscale italiana e dai default azienda.
          Le voci marcate <span className="text-[10px] text-slate-400">🔒 fisso</span> non sono
          modificabili da qui. Le voci dinamiche (Competenze agenzia, Spese spedizione) seguono
          il fornitore selezionato a livello di singola vendita.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {lockedRows.map((r) => (
            <div key={r.key}>
              <Label className="text-xs">{r.label}</Label>
              <div className="flex items-center gap-2 h-9 px-3 bg-slate-100 border border-slate-200 rounded">
                <span className="font-mono text-sm">{config[r.key] || r.expected}</span>
                <span className="text-[10px] text-slate-400">🔒 fisso</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">{r.desc}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
          <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2.5">
            <span className="font-semibold text-blue-900">Competenze agenzia</span>
            <p className="text-blue-800 mt-1">
              ⓘ IVA calcolata in vendita dal fornitore agenzia disbrigo selezionato.
              Default da <code>anagrafica.iva_codice</code>, fallback IVA azienda.
            </p>
          </div>
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2.5">
            <span className="font-semibold text-slate-800">Spese spedizione</span>
            <p className="text-slate-700 mt-1">
              ⓘ Escluse da fatturazione IVA. Per rifatturare in IVA, sposta l'importo nelle Competenze.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
